import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, EMPTY, map, Observable, take } from 'rxjs';

import {
  PortalServiceClassType,
  PortalOrderNode,
  PortalOrderStatusType,
  PortalFeatures,
  AncillaryTypeEnum,
} from '@app/services/api/api.types';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { State, StateService } from '@app/services/state/state.service';
import { PortalQueryParams, PortalRoutes } from '@app/app-routing.module';
import { PopupsService } from '@voyagu/portal-design-system';
import { ApiService } from '@app/services/api/api.service';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { ErrorService } from '@app/services/error.service';
import { handleError } from '@app/operators/handle-error';
import {
  getOptionSoldAmount,
  isEmbeddedInIframe,
  shouldHideButtonsInEmbeddedMode,
} from '@app/utils/utils';
import { isTiersFeature } from '@app/pages/tiers-page/utils';
import {
  checkoutExtrasFromState,
  SessionProceedCoordinator,
} from '@app/services/session-proceed-coordinator.service';
@Component({
  selector: 'option-page',
  templateUrl: './option-page.component.html',
  styleUrl: './option-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class OptionPageComponent implements OnInit {
  portalServiceClassType = PortalServiceClassType;

  withPayAndConfirm: boolean;
  portalTiers: boolean;
  portalSequentiallyAncillaries = false;

  isNewUser = false;

  hideEmbeddedButtons = isEmbeddedInIframe();

  portalOrder$: Observable<PortalOrderNode | null>;

  portalOrder: PortalOrderNode;

  state: State;

  @ViewChild('hiddenFlightsTemplate') hiddenFlightsTemplate: TemplateRef<unknown>;
  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  sessionProceed = inject(SessionProceedCoordinator);

  constructor(
    protected portalLeadService: PortalLeadService,
    private portalOrderService: PortalOrderService,
    private stateService: StateService,
    private popupsService: PopupsService,
    private errorService: ErrorService,
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
  ) {}

  ngOnInit() {
    this.portalOrder$ = combineLatest([
      this.portalLeadService.portalLead$,
      this.portalOrderService.portalOrder$,
    ]).pipe(
      map(([portalLead, portalOrder]) => {
        if (portalLead) {
          this.withPayAndConfirm = !portalOrder?.isDemo;

          this.isNewUser = !!portalLead.isNewUser;

          this.portalTiers =
            portalLead.salesAgent?.portalFeatures?.includes(PortalFeatures.portal_tiers) &&
            isTiersFeature(portalOrder);
          this.portalSequentiallyAncillaries = !!portalLead.salesAgent?.portalFeatures?.includes(
            PortalFeatures.portal_sequentially_ancillaries,
          );
        }

        if (portalOrder) {
          this.portalOrder = portalOrder;
        }

        this.hideEmbeddedButtons = this.portalOrder
          ? shouldHideButtonsInEmbeddedMode(this.computeThirdSegmentForProceedToCheckout())
          : isEmbeddedInIframe();

        return portalOrder;
      }),
    );

    this.state = this.stateService.getState();
  }

  refetchLead() {
    const portalLink = this.stateService.getState().portalLink;

    this.portalLeadService.getPortalLead({ portalLink });
  }

  showPresentHiddenFlightsModal() {
    this.popupsService.showModal(this.hiddenFlightsTemplate, { className: 'wide' });
  }

  requestToBook(portalOrder: PortalOrderNode) {
    this.portalOrderService.setPortalOrder(portalOrder);
    this.portalOrder = portalOrder;

    const needsToPresentHiddenFlights =
      (this.portalOrder.hasHiddenCityTicketing || this.portalOrder.hasThrowawayTicketing) &&
      !this.portalOrder.hiddenFlightsPresented;

    if (needsToPresentHiddenFlights) {
      return this.showPresentHiddenFlightsModal();
    }

    const soldAmount = getOptionSoldAmount(this.portalOrder);

    if (this.portalOrder.isChange && soldAmount < 0) {
      return this.submitRequestToBook();
    }

    const gateSignIn = this.computeThirdSegmentForProceedToCheckout() === PortalRoutes.Travelers;

    this.sessionProceed.runAfterSessionCheckIf(
      gateSignIn,
      this.isNewUser,
      () => this.submitRequestToBook(),
      () => this.showAskToSignInModal(),
    );
  }

  hideModal() {
    this.popupsService.hideLast();
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  hideAskToSignInModal() {
    this.sessionProceed.dismissAskModal(() => this.popupsService.hideLast());
  }

  continueAsGuestFromSignInPrompt() {
    this.sessionProceed.continueAsGuestAfterPrompt(() => this.popupsService.hideLast());
  }

  showSignInModal() {
    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      {
        ...checkoutExtrasFromState(this.stateService.getState()),
        checkoutResume: this.computeThirdSegmentForProceedToCheckout(),
        checkoutPath: 'option',
      },
    );
  }

  portalUpdateOption() {
    const variables = {
      inputUpdate: {
        portalLink: this.portalOrder.portalLink,
        status: PortalOrderStatusType.OptionBooked,
      },
    };

    return this.apiService
      .portalUpdateOption(variables)
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.hideModal();

        if (response?.ok) {
          const portalLink = this.stateService.getState().portalLink;

          this.portalLeadService.getPortalLead({ portalLink });

          void this.router.navigate(
            [`${portalLink}`, `${this.portalOrder.portalLink}`, PortalRoutes.Complete],
            { queryParamsHandling: 'merge' },
          );
        } else {
          this.showErrorModal();
        }
      });
  }

  showErrorModal() {
    this.errorService.showCommonError();
  }

  back() {
    this.location.back();
  }

  computeThirdSegmentForProceedToCheckout(): PortalRoutes {
    const hasAncillaries = (this.portalOrder.ancillaries?.length ?? 0) > 0;

    const isSequential = this.portalSequentiallyAncillaries;

    const hasDisruption = this.portalOrder.ancillaries.some(
      (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
    );

    const ancillaryPath = isSequential
      ? hasDisruption
        ? PortalRoutes.DisruptionProtection
        : PortalRoutes.CancelForAnyReason
      : PortalRoutes.AncillaryBundleComponent;

    const ancillaryRoute = this.portalTiers ? PortalRoutes.Tiers : ancillaryPath;

    const changeRoute = this.portalTiers
      ? PortalRoutes.TierChange
      : hasAncillaries
        ? ancillaryPath
        : PortalRoutes.Gratuity;

    const routedSegment = this.portalOrder.isChange
      ? changeRoute
      : hasAncillaries
        ? ancillaryRoute
        : PortalRoutes.Travelers;

    return this.withPayAndConfirm ? routedSegment : PortalRoutes.Travelers;
  }

  submitRequestToBook() {
    const soldAmount = getOptionSoldAmount(this.portalOrder);

    if (this.portalOrder.isChange && soldAmount < 0) {
      this.portalUpdateOption();
      return;
    }

    const shouldOptionSelect =
      this.activatedRoute.snapshot.queryParamMap.get(PortalQueryParams.UtmSource) !==
      'agent_preview';

    if (shouldOptionSelect) {
      this.optionSelected();
    }

    const thirdSegment = this.computeThirdSegmentForProceedToCheckout();

    const path = [`${this.state.portalLink}`, `${this.portalOrder.portalLink}`, thirdSegment];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  optionSelected() {
    this.apiService
      .portalOptionSelected({ portalLink: this.portalOrder.portalLink })
      .pipe(take(1), handleError(this.errorService))
      .subscribe();
  }
}
